import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Modal,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  jids,
  yesNoOptions,
} from '../../../data/forms/generalDropdownOptions.js';
import moment from 'moment/moment';
import axios from 'axios';
import { useAuth } from '../../../context/authContext.js';

export default function EditRepairOrderDetailsModal({
  isOpen,
  handleClose,
  entry,
  handleRefresh,
  setIsSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
}) {
  const { user } = useAuth();

  const [rmaNumber, setRmaNumber] = useState(
    entry.rmaNumber != null ? String(entry.rmaNumber) : '',
  );

  const [rrfRepair, setRrfRepair] = useState(
    entry.rrfRepair ? entry.rrfRepair : '',
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Same rule as repair order search: RRF Repair is hidden when OEM is Yes, so we do not edit it there.
  const showRrfRepair = entry.oem !== 'Yes';

  // Values from `entry` as loaded from the API, normalized to match controlled field types (string / '').
  function entryRmaInitial() {
    return entry.rmaNumber != null ? String(entry.rmaNumber) : '';
  }

  function entryRrfInitial() {
    return entry.rrfRepair ? entry.rrfRepair : '';
  }

  // Update stays disabled until the user changes at least one editable field vs the original `entry`.
  const isFormValid = () => {
    const rmaDirty = rmaNumber !== entryRmaInitial();
    const rrfDirty = showRrfRepair && rrfRepair !== entryRrfInitial();
    return rmaDirty || rrfDirty;
  };

  async function submit() {
    try {
      setIsSubmitting(true);

      await axios({
        method: 'PUT',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/depot/benchrepair/updateRepairLog`,
        data: {
          repairOrder: entry.repairOrder,
          rmaNumber: rmaNumber.trim() || null,
          rrfRepair: showRrfRepair
            ? rrfRepair
            : entry.rrfRepair != null
              ? entry.rrfRepair
              : null,
          userId: user.userId,
          displayName: user.displayName,
        },
      });
      setSnackbarMessage('Repair Order updated successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error submitting repair order edit:', error);
      setSnackbarMessage(
        'There was an error submitting the repair order edit. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Grid
        container
        spacing={2}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95vw', sm: '90vw', md: 800 },
          bgcolor: 'var(--dust-white)',
          p: 4,
          overflowY: 'auto',
          maxHeight: { xs: '90vh', md: '100%' },
        }}
      >
        <Grid size={12}>
          <Typography
            id='modal-modal-title'
            sx={{ fontFamily: 'BrightstarSans-Regular', fontSize: '25px' }}
          >
            {'Edit Repair Order Details'}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            disabled={true}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>{'Jurisdiction'}</InputLabel>
            <Select label={'Jurisdiction'} value={entry.jurisdiction}>
              {jids.map(value => {
                return <MenuItem value={value}>{value}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <TextField
            label='Case Number'
            value={entry.caseNumber != null ? String(entry.caseNumber) : ''}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            disabled={true}
          />
          <FormControl
            disabled={true}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>OEM</InputLabel>
            <Select label={'OEM'} value={entry.oem}>
              {yesNoOptions.map(value => {
                return <MenuItem value={value}>{value}</MenuItem>;
              })}
            </Select>
          </FormControl>
          {showRrfRepair && (
            <FormControl
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>RRF Repair</InputLabel>
              <Select
                label={'RRF Repair'}
                value={rrfRepair}
                onChange={event => setRrfRepair(event.target.value)}
              >
                {yesNoOptions.map(value => {
                  return <MenuItem value={value}>{value}</MenuItem>;
                })}
              </Select>
            </FormControl>
          )}
          <TextField
            label='Created Date'
            value={
              entry.dateTime
                ? moment(entry.dateTime).format('YYYY-MM-DD')
                : 'N/A'
            }
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            disabled={true}
          />
          <TextField
            label='Comments'
            value={entry.comments ? entry.comments : ''}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            disabled={true}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label='Repair Order Number'
            value={entry.repairOrder}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            disabled={true}
          />
          <TextField
            label='RMA Number'
            value={rmaNumber}
            onChange={event => setRmaNumber(event.target.value)}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <FormControl
            disabled={true}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>Open Case</InputLabel>
            <Select label={'Open Case'} value={entry.openCase}>
              {yesNoOptions.map(value => {
                return <MenuItem value={value}>{value}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <TextField
            label='Created By'
            value={entry.displayName ? entry.displayName : ''}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            disabled={true}
          />
        </Grid>
        <Grid
          size={12}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button
            variant='contained'
            sx={{ marginRight: '10px', backgroundColor: 'var(--arcturus-red)' }}
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            disabled={!isFormValid() || isSubmitting}
            variant='contained'
            sx={{ backgroundColor: 'var(--vega-blue)' }}
            onClick={submit}
          >
            {'Update'}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
}
